import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { PlusCircle, Edit, Trash2, Search, Home, Eye, QrCode, ScanLine, Printer, PackageCheck, CircleHelp } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode';
import PVSPageLayout from './PVSPageLayout';
import { isCalorOwner } from '../utils/calorOwner';

const SCRAP_PREP_OPTIONS = ['Calor Prepared', 'PVS Prepared', 'PVS Inspection Failure'];
const LOCKED_SCRAP_PREP = new Set(['Calor Prepared', 'PVS Prepared']);

const SCRAP_PREP_HELP = [
  {
    title: 'Calor Prepared vessels',
    body: 'Vessels that have come de-valved and issued as gas free from Calor Gas depots. They are only required to be decommissioned by cutting out a boss and the data plate, keeping the physical data plate for our records. These vessels will have had a C1 form completed internally by Calor Gas. Vessels must be labelled and clearly identified as scrap and segregated.',
  },
  {
    title: 'PVS Prepared vessels',
    body: 'Vessels that have been collected specifically to be degassed, de-valved and scrapped on behalf of Calor Gas. These vessels require gas-free certification prior to decommissioning by cutting out a boss and the data plate, keeping the physical data plate for our records. These vessels will have had a C1 form completed internally by Calor Gas. Vessels must be labelled and clearly identified as scrap and segregated.',
  },
  {
    title: 'PVS Inspection Failure',
    body: 'Vessels that arrived at PVS for other work, such as thorough testing, in-service testing or turnaround, but have since failed their inspection because they did not meet the vessel rejection criteria set out in CAL515. A PVS Vessel Failure report must be issued, and the vessels must be labelled and clearly identified as scrap and segregated. Further to Calor Gas carrying out a C1 form, these vessels must remain whole until all investigation works have been completed.',
  },
];

const formatDisplayDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (!(date instanceof Date) || isNaN(date)) return '';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
};

const LPGInventorySystem = ({ onReturnToPVSHomePage, isAdmin, userRole = 'standard' }) => {

    const canAdd = userRole !== 'readonly';
    const canEdit = userRole === 'standard' || isAdmin;
    const canDelete = isAdmin;

    console.log('Component rendering');

    const API_URL = 'https://app.pvslimited.co.uk/api';

const getAuthHeaders = (includeContentType = false) => {
  const token = localStorage.getItem('token');
  const headers = {};

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

// Define API object
const api = useMemo(() => ({
  fetchVessels: async () => {
    try {
      console.log('Attempting to fetch vessels...');
      const response = await fetch(`${API_URL}/vessels?limit=10`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error('Failed to fetch vessels');
      }
      const data = await response.json();
      console.log('Fetched vessels:', data);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error loading vessels:', error);
      return [];
    }
  },
    
    searchVessels: async (searchCriteria) => {
        try {
            const response = await fetch(`${API_URL}/vessels/search`, {
                method: 'POST',
                headers: getAuthHeaders(true),
                body: JSON.stringify(searchCriteria)
            });
            if (!response.ok) throw new Error('Search failed');
            return await response.json();
        } catch (error) {
            console.error('Error searching vessels:', error);
            return [];
        }
    },
    
    addVessel: async (vesselData) => {
        try {
            const response = await fetch(`${API_URL}/vessels`, {
                method: 'POST',
                headers: getAuthHeaders(true),
                body: JSON.stringify(vesselData)
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to add vessel');
            }
            return await response.json();
        } catch (error) {
            console.error('Error adding vessel:', error);
            throw error;
        }
    },
    
    updateVessel: async (id, vesselData) => {
      try {
        const response = await fetch(`${API_URL}/vessels/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(true),
            body: JSON.stringify(vesselData)
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update vessel');
        }
        return await response.json();
    } catch (error) {
        console.error('Error updating vessel:', error);
        throw error;
    }
    },
    
    deleteVessel: async (id) => {
      try {
        const response = await fetch(`${API_URL}/vessels/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to delete vessel');
        }
        return await response.json();
    } catch (error) {
        console.error('Error deleting vessel:', error);
        throw error;
    }
  },

  fetchVesselAudit: async (serialNumber) => {
    try {
      const response = await fetch(
        `${API_URL}/vessels/${encodeURIComponent(serialNumber)}/audit?limit=200`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to load vessel audit history');
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading vessel audit history:', error);
      throw error;
    }
  }
  }),[]);

  // State declarations
// Normalize a serial number for comparison: lowercase and strip any character
// that isn't a letter or digit. e.g. "Lg-1234", "lg 1234" and "lg1234" all
// normalize to "lg1234" so they are treated as the same serial number.
const normalizeSerialNumber = (value) => (
    String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '')
);

// Initial form state
const initialFormState = {
    vesselSize: '',
    serialNumber: '',
    vesselManufacturer: '',
    designPressure: '',
    dateOfManufacture: '',
    manufacturingDate: '',
    lastTestDate: '',
    nextTestDue: '',
    dateReceived: '',
    dateReturned: '',
    dateRefurbed: '',
    owner: '',
    vesselTypeCode: '',
    calorSerialNumber: '',
    workDone: '',
    certificateNumber: '',
    engineerComments: '',
    refurbStatus: 'Pending',
    scrapPreparation: '',
    scrapStatusChangeReason: '',
    scrapPreparationChangeReason: ''
};


// States
const [items, setItems] = useState([]);
const [viewingItem, setViewingItem] = useState(null);
const [showAddDialog, setShowAddDialog] = useState(false);
const [editingItem, setEditingItem] = useState(null);
const [searchCriteria, setSearchCriteria] = useState([{ field: 'serialNumber', value: '' }]);
const [isSubmitting, setIsSubmitting] = useState(false);
const [errors, setErrors] = useState({});
const [formData, setFormData] = useState(initialFormState);
const [auditHistory, setAuditHistory] = useState([]);
const [isAuditLoading, setIsAuditLoading] = useState(false);
const [auditError, setAuditError] = useState('');
const [showScanner, setShowScanner] = useState(false);
const [scanError, setScanError] = useState('');
const [isBookingOut, setIsBookingOut] = useState(false);
const [openedViaQrScan, setOpenedViaQrScan] = useState(false);
const [showSerialConfirm, setShowSerialConfirm] = useState(false);
const [serialConfirmValue, setSerialConfirmValue] = useState('');
const [serialConfirmError, setSerialConfirmError] = useState('');
const [showScrapPrepHelp, setShowScrapPrepHelp] = useState(false);
const scannerRef = useRef(null);
const scannerDivId = 'qr-reader';
const serialInputRef = useRef(null);
const calorSerialInputRef = useRef(null);
const serialConfirmInputRef = useRef(null);
const formSnapshotRef = useRef(null);

const ownerIsCalor = useMemo(() => isCalorOwner(formData.owner), [formData.owner]);

const editingScrappedVessel = editingItem?.refurbStatus === 'Scrap';
const leavingScrap = editingScrappedVessel && formData.refurbStatus !== 'Scrap';
const showScrapPrep = ownerIsCalor && formData.refurbStatus === 'Scrap';
const savedScrapPrep = editingItem?.scrapPreparation || '';
const scrapPrepIsLocked = LOCKED_SCRAP_PREP.has(savedScrapPrep);
const scrapPrepLockedForUser = Boolean(editingItem) && scrapPrepIsLocked && !isAdmin;
const scrapPrepWillChange = Boolean(editingItem) && scrapPrepIsLocked && (
  showScrapPrep ? (formData.scrapPreparation || '') !== savedScrapPrep : true
);
const needsScrapPrepReason = scrapPrepWillChange && isAdmin && !leavingScrap;

// Calor vessels are verified against their Calor serial number, everything
// else is verified against the standard serial number.
const serialFieldToConfirm = ownerIsCalor ? 'calorSerialNumber' : 'serialNumber';

const getFormStatus = (item, key) => item?.formProgress?.[key] || { completed: false, completedAt: null };

const renderFormTick = (item, key) => {
  const status = getFormStatus(item, key);
  if (!status.completed) return <span className="text-gray-400">-</span>;
  const dateText = formatDisplayDate(status.completedAt);

  return (
    <span className="text-pvs-accent font-medium">
      ✓ {dateText}
    </span>
  );
};

  const refurbStatusOptions = [
    'Pending',
    'Work Required',
    'Painted',
    'Valved',
    'Gas Charged',
    'Done',
    'Washed Down',
    'Nothing Required',
    '10 year test',
    'Scrap',
    'Dispatched',
    'De-valved'
  ];

  useEffect(() => {
    const loadVessels = async () => {
        try {
            console.log('Attempting to fetch vessels...');
            const vessels = await api.fetchVessels();
            console.log('Fetched vessels:', vessels);
            setItems(vessels);
        } catch (error) {
            console.error('Error loading vessels:', error);
            alert('Failed to load vessels');
        }
    };

    loadVessels();
}, [api]);

  // Navigation handler
  const handleHomeNavigation = (e) => {
    e.preventDefault();
    console.log('Home button clicked');
    if (onReturnToPVSHomePage) {
      onReturnToPVSHomePage();
    } else {
      console.error('onReturnToPVSHomePage prop not provided');
    }
  };

  // Search functionality
  const handleSearchFieldChange = (index, key, value) => {
    setSearchCriteria(prev => {
      const newCriteria = [...prev];
      newCriteria[index] = {
        ...newCriteria[index],
        [key]: value
      };
      return newCriteria;
    });
  };

  const addSearchField = () => {
    setSearchCriteria(prev => [...prev, { field: 'serialNumber', value: '' }]);
  };

  const removeSearchField = (index) => {
    setSearchCriteria(prev => prev.filter((_, i) => i !== index));
  };

  const clearSearch = () => {
    setSearchCriteria([{ field: 'serialNumber', value: '' }]);
    setItems([]);
  };

 

  



    // Validation function
   // Validation function
const validateForm = () => {
  const newErrors = {};

  // Required fields validation
  if (!formData.serialNumber?.trim()) {
      newErrors.serialNumber = 'Serial Number is required';
  }

  // Size validation for numerical values only
  if (!formData.vesselSize) {
      newErrors.vesselSize = 'Size is required';
  } else {
      const numericalValue = parseInt(formData.vesselSize.toString());
      if (isNaN(numericalValue)) {
          newErrors.vesselSize = 'Size must be a numerical value';
      } else if (numericalValue <= 0) {
          newErrors.vesselSize = 'Size must be greater than 0';
      }
  }

  if (!formData.owner?.trim()) {
      newErrors.owner = 'Owner is required';
  }

  if (ownerIsCalor) {
      if (formData.vesselTypeCode === '' || formData.vesselTypeCode === null || formData.vesselTypeCode === undefined) {
          newErrors.vesselTypeCode = 'Vessel Type Code is required for Calor vessels';
      } else if (Number.isNaN(Number(formData.vesselTypeCode))) {
          newErrors.vesselTypeCode = 'Vessel Type Code must be a numerical value';
      }
      if (!formData.calorSerialNumber?.trim()) {
          newErrors.calorSerialNumber = 'Calor Serial Number is required for Calor vessels';
      }
  }

  // Vessel Manufacturer and Design Pressure are mandatory for NEW vessels only.
  // Existing vessels can be edited without these fields being populated.
  if (!editingItem) {
      if (!formData.vesselManufacturer?.trim()) {
          newErrors.vesselManufacturer = 'Vessel Manufacturer is required';
      }
      if (formData.designPressure === '' || formData.designPressure === null || formData.designPressure === undefined) {
          newErrors.designPressure = 'Design Pressure is required';
      } else {
          const dpNum = parseFloat(formData.designPressure.toString());
          if (isNaN(dpNum)) {
              newErrors.designPressure = 'Design Pressure must be a numerical value';
          } else if (dpNum <= 0) {
              newErrors.designPressure = 'Design Pressure must be greater than 0';
          }
      }
      if (!formData.dateOfManufacture) {
          newErrors.dateOfManufacture = 'Date of Manufacture is required';
      } else {
          const manufactureDate = new Date(formData.dateOfManufacture);
          if (manufactureDate > new Date()) {
              newErrors.dateOfManufacture = 'Date of Manufacture cannot be in the future';
          }
      }
  } else if (formData.designPressure !== '' && formData.designPressure !== null && formData.designPressure !== undefined) {
      const dpNum = parseFloat(formData.designPressure.toString());
      if (isNaN(dpNum)) {
          newErrors.designPressure = 'Design Pressure must be a numerical value';
      }
  }

  if (leavingScrap) {
      if (!isAdmin) {
          newErrors.refurbStatus = 'Only an admin can change the refurb status of a scrapped vessel';
      } else if (!formData.scrapStatusChangeReason?.trim()) {
          newErrors.scrapStatusChangeReason = 'A reason is required to change the status of a scrapped vessel';
      }
  }

  if (showScrapPrep && !scrapPrepLockedForUser && !formData.scrapPreparation) {
      newErrors.scrapPreparation = 'Select Calor Prepared, PVS Prepared or PVS Inspection Failure';
  }
  if (scrapPrepWillChange && !isAdmin) {
      newErrors.scrapPreparation = 'Only an admin can change Calor Prepared or PVS Prepared';
  }
  if (needsScrapPrepReason && !formData.scrapPreparationChangeReason?.trim()) {
      newErrors.scrapPreparationChangeReason = 'A reason is required. It is added to the engineer comments';
  }

  // Date validations
  if (!formData.dateReceived) {
      newErrors.dateReceived = 'Date Received is required';
  }

  // Validate refurb date against received date
  if (formData.dateReceived && formData.dateRefurbed) {
      const refurbedDate = new Date(formData.dateRefurbed);
      const receivedDate = new Date(formData.dateReceived);

      if (refurbedDate < receivedDate) {
          newErrors.dateRefurbed = 'Refurb date cannot be before received date';
      }
  }

  // Validate returned date against received date
  if (formData.dateReceived && formData.dateReturned) {
      const returnedDate = new Date(formData.dateReturned);
      const receivedDate = new Date(formData.dateReceived);

      if (returnedDate < receivedDate) {
          newErrors.dateReturned = 'Return date cannot be before received date';
      }
  }

  // Future date validation
  const currentDate = new Date();
  if (formData.dateReceived) {
      const receivedDate = new Date(formData.dateReceived);
      if (receivedDate > currentDate) {
          newErrors.dateReceived = 'Received date cannot be in the future';
      }
  }

  if (formData.dateReturned) {
      const returnedDate = new Date(formData.dateReturned);
      if (returnedDate > currentDate) {
          newErrors.dateReturned = 'Return date cannot be in the future';
      }
  }

  if (formData.dateRefurbed) {
      const refurbedDate = new Date(formData.dateRefurbed);
      if (refurbedDate > currentDate) {
          newErrors.dateRefurbed = 'Refurb date cannot be in the future';
      }
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

    // Input change handler
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when field is edited
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

  
 // Submitting a new vessel is gated behind a serial number re-entry check.
 const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) {
      console.log('Form validation failed');
      const firstError = document.querySelector('.text-red-400');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
  }

  if (!editingItem) {
      formSnapshotRef.current = formData;
      setSerialConfirmValue('');
      setSerialConfirmError('');
      setShowSerialConfirm(true);
      return;
  }

  await saveVessel();
};

// Re-typed serial must match the entered one exactly, otherwise the entered
// value is discarded and the user has to key the whole number in again.
const handleSerialConfirmSubmit = async (e) => {
  e.preventDefault();

  const expected = formData[serialFieldToConfirm]?.trim() || '';
  if (serialConfirmValue.trim() !== expected) {
      setShowSerialConfirm(false);
      setSerialConfirmValue('');
      setSerialConfirmError('');
      setFormData(prev => ({ ...(formSnapshotRef.current || prev), [serialFieldToConfirm]: '' }));
      setErrors(prev => ({
          ...prev,
          [serialFieldToConfirm]: 'Confirmation did not match. Enter the serial number again.'
      }));
      setTimeout(() => {
          const target = ownerIsCalor ? calorSerialInputRef.current : serialInputRef.current;
          target?.focus();
          target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
  }

  setShowSerialConfirm(false);
  setSerialConfirmValue('');
  setSerialConfirmError('');
  await saveVessel();
};

const blockClipboard = (e) => {
  e.preventDefault();
  setSerialConfirmError('Copy and paste is disabled - type the serial number in full.');
};

// A browser or password-manager form reset puts the refurb status select back
// to its first option (Pending) on screen while React state keeps the real value.
const preventFormReset = (e) => {
  e.preventDefault();
};

const saveVessel = async () => {
  setIsSubmitting(true);

  try {
      console.log('Initial form data:', formData);

      // Prepare dates
      const prepareDate = (dateString) => {
          if (!dateString) return null;
          const date = new Date(dateString);
          return date instanceof Date && !isNaN(date) ? date : null;
      };

      // Explicitly create the vessel data object
      const effectiveRefurbStatus = prepareDate(formData.dateReturned) && formData.refurbStatus !== 'Scrap'
          ? 'Dispatched'
          : (formData.refurbStatus || 'Pending');
      const sendScrapPrep = ownerIsCalor && effectiveRefurbStatus === 'Scrap';

      const vesselToSave = {
          serialNumber: formData.serialNumber?.trim() || '',
          vesselSize: formData.vesselSize?.toString() || '',
          owner: formData.owner?.trim() || '',
          vesselTypeCode: ownerIsCalor ? Number(formData.vesselTypeCode) : null,
          calorSerialNumber: ownerIsCalor ? formData.calorSerialNumber?.trim() || '' : '',
          refurbStatus: effectiveRefurbStatus,
          dateReceived: prepareDate(formData.dateReceived),
          dateReturned: prepareDate(formData.dateReturned),
          dateRefurbed: prepareDate(formData.dateRefurbed),
          workDone: formData.workDone?.trim() || '',
          engineerComments: formData.engineerComments?.trim() || '',
          certificateNumber: formData.certificateNumber?.trim() || '',
          scrapPreparation: sendScrapPrep
              ? (scrapPrepLockedForUser ? savedScrapPrep : (formData.scrapPreparation || ''))
              : ''
      };

      if (leavingScrap) {
          vesselToSave.scrapStatusChangeReason = formData.scrapStatusChangeReason.trim();
      }
      if (needsScrapPrepReason) {
          vesselToSave.scrapPreparationChangeReason = formData.scrapPreparationChangeReason.trim();
      }

      // Only include Vessel Manufacturer / Design Pressure if provided.
      // For new vessels these are mandatory (enforced by validateForm above).
      // For existing vessels they remain optional so legacy records can still be saved.
      if (formData.vesselManufacturer && formData.vesselManufacturer.trim() !== '') {
          vesselToSave.vesselManufacturer = formData.vesselManufacturer.trim();
      }
      if (formData.designPressure !== '' && formData.designPressure !== null && formData.designPressure !== undefined) {
          vesselToSave.designPressure = Number(formData.designPressure);
      }
      if (formData.dateOfManufacture) {
          vesselToSave.dateOfManufacture = prepareDate(formData.dateOfManufacture);
      }

      console.log('Prepared vessel data:', vesselToSave);

      // Validate required fields
      if (!vesselToSave.serialNumber || !vesselToSave.vesselSize || !vesselToSave.owner) {
          console.error('Required fields missing:', {
              serialNumber: !vesselToSave.serialNumber,
              vesselSize: !vesselToSave.vesselSize,
              owner: !vesselToSave.owner
          });
          throw new Error('Required fields missing');
      }

      let savedVessel;
      if (editingItem) {
          savedVessel = await api.updateVessel(editingItem._id, vesselToSave);
      } else {
          const normalizedNewSerial = normalizeSerialNumber(vesselToSave.serialNumber);
          const existing = items.find(item =>
              normalizeSerialNumber(item.serialNumber) === normalizedNewSerial
          );
          
          if (existing) {
              setErrors(prev => ({
                  ...prev,
                  serialNumber: 'This serial number already exists'
              }));
              setIsSubmitting(false);
              return;
          }

          savedVessel = await api.addVessel(vesselToSave);
      }

      setItems(prev => editingItem 
          ? prev.map(item => item._id === editingItem._id ? savedVessel : item)
          : [...prev, savedVessel]
      );

      alert(editingItem ? 'Vessel updated successfully' : 'New vessel added successfully');
      setFormData(initialFormState);
      setShowAddDialog(false);
      setEditingItem(null);
      setShowScrapPrepHelp(false);

  } catch (error) {
      console.error('Submission error:', error);
      alert(`Error: ${error.message}`);
  } finally {
      setIsSubmitting(false);
  }
};
  // Search handlers
  const handleSearch = async () => {
    const validCriteria = searchCriteria.filter(c => c.field && c.value);
    
    console.log('Search criteria:', validCriteria);

    if (validCriteria.length === 0) {
        alert('Please select at least one search field and enter a search value');
        return;
    }

    try {
        console.log('Making search request with criteria:', validCriteria);
        // Use the api object for search
        const results = await api.searchVessels(validCriteria);
        console.log('Search results:', results);
        setItems(results);
    } catch (error) {
        console.error('Search failed:', error);
        alert('An error occurred while searching. Please try again.');
    }
};
 

  // CRUD operations
  const handleEdit = (item) => {
    // Format dates to YYYY-MM-DD for input[type="date"]
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date) 
            ? date.toISOString().split('T')[0] 
            : '';
    };
    
    setEditingItem(item);
    setFormData({
        vesselSize: item.vesselSize,
        serialNumber: item.serialNumber,
        vesselManufacturer: item.vesselManufacturer || '',
        designPressure: item.designPressure ?? '',
        dateOfManufacture: formatDate(item.dateOfManufacture),
        manufacturingDate: item.manufacturingDate || '',
        lastTestDate: item.lastTestDate || '',
        nextTestDue: item.nextTestDue || '',
        dateReceived: formatDate(item.dateReceived),
        dateRefurbed: formatDate(item.dateRefurbed),
        dateReturned: formatDate(item.dateReturned),
        owner: item.owner,
        vesselTypeCode: item.vesselTypeCode ?? '',
        calorSerialNumber: item.calorSerialNumber || '',
        workDone: item.workDone || '',
        certificateNumber: item.certificateNumber || '',
        engineerComments: item.engineerComments || '',
        refurbStatus: item.refurbStatus || 'Pending',
        scrapPreparation: item.scrapPreparation || '',
        scrapStatusChangeReason: '',
        scrapPreparationChangeReason: ''
    });
    setShowAddDialog(true);
};

  const handleDelete = async (id) => {
    if (!isAdmin) {
      alert('Only administrators can delete vessel records');
      return;
    }
    if (window.confirm('Are you sure you want to delete this vessel record? This action cannot be undone.')) {
        try {
          console.log('Deleting vessel with ID:', id); // Debug log
          await api.deleteVessel(id);
          setItems(prev => prev.filter(item => item._id !== id));
          alert('Vessel deleted successfully');
        } catch (error) {
          console.error('Delete failed:', error);
          alert('Failed to delete the vessel record. Please try again.');
        }
      }
    };
  
  const handleViewDetails = async (item) => {
    setViewingItem(item);
    setAuditHistory([]);
    setAuditError('');

    if (!isAdmin && userRole !== 'readonly') return;

    setIsAuditLoading(true);
    try {
      const logs = await api.fetchVesselAudit(item.serialNumber);
      setAuditHistory(Array.isArray(logs) ? logs : []);
    } catch (error) {
      setAuditError(error.message || 'Failed to load audit history');
    } finally {
      setIsAuditLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewingItem(null);
    setAuditHistory([]);
    setAuditError('');
    setIsAuditLoading(false);
    setOpenedViaQrScan(false);
  };

  const startScanner = useCallback(async () => {
    setScanError('');
    try {
      const html5Qrcode = new Html5Qrcode(scannerDivId);
      scannerRef.current = html5Qrcode;
      await html5Qrcode.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          await html5Qrcode.stop();
          scannerRef.current = null;
          setShowScanner(false);
          const results = await api.searchVessels([{ field: 'serialNumber', value: decodedText.trim() }]);
          if (results.length > 0) {
            setOpenedViaQrScan(true);
            handleViewDetails(results[0]);
          } else {
            alert(`No vessel found with serial number: ${decodedText}`);
          }
        },
        () => {}
      );
    } catch (err) {
      setScanError('Unable to access camera. Please ensure camera permissions are granted.');
    }
  }, [api]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch (e) { /* scanner may already be stopped */ }
      scannerRef.current = null;
    }
    setShowScanner(false);
    setScanError('');
  }, []);

  const handlePrintQR = (serialNumber) => {
    const printWindow = window.open('', '_blank', 'width=400,height=500');
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - ${serialNumber}</title>
          <style>
            body { display:flex; flex-direction:column; align-items:center; justify-content:center; height:100vh; margin:0; font-family:Arial,sans-serif; }
            svg { width:200px; height:200px; }
            p { margin-top:12px; font-size:18px; font-weight:bold; letter-spacing:1px; }
          </style>
        </head>
        <body>
          ${document.getElementById('qr-code-svg-container')?.innerHTML || ''}
          <p>${serialNumber}</p>
          <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();}}<\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const allFormsComplete = (item) => {
    const keys = ['preInspection', 'paintQualityInspection', 'tankCertification', 'finalQCChecklist'];
    return keys.every(k => item?.formProgress?.[k]?.completed === true);
  };

  const getIncompleteFormNames = (item) => {
    const formLabels = {
      preInspection: 'Pre Inspection',
      paintQualityInspection: 'Paint Quality Inspection',
      tankCertification: 'Tank Certification',
      finalQCChecklist: 'Final QC Checklist',
    };
    return Object.entries(formLabels)
      .filter(([key]) => !item?.formProgress?.[key]?.completed)
      .map(([, label]) => label);
  };

  const handleBookOut = async (item) => {
    if (!allFormsComplete(item)) return;
    const confirmed = window.confirm(
      `Are you sure you want to book out vessel ${item.serialNumber}? This will set the return date to today and send a notification email.`
    );
    if (!confirmed) return;

    setIsBookingOut(true);
    try {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      const updatedVessel = await api.updateVessel(item._id, {
        dateReturned: today.toISOString(),
        refurbStatus: item.refurbStatus === 'Scrap' ? 'Scrap' : 'Dispatched'
      });

      await fetch(`${API_URL}/send-email`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          to: 'forms@pvslimited.com',
          subject: `Vessel Booked Out - ${item.serialNumber} - ${dateStr}`,
          html: `
            <h2>Vessel Booked Out</h2>
            <table style="border-collapse:collapse;width:100%;max-width:500px;">
              <tr><td style="padding:8px;border:1px solid #ddd;font-weight:bold;">Serial Number</td><td style="padding:8px;border:1px solid #ddd;">${item.serialNumber}</td></tr>
              <tr><td style="padding:8px;border:1px solid #ddd;font-weight:bold;">Size</td><td style="padding:8px;border:1px solid #ddd;">${item.vesselSize} Litres</td></tr>
              <tr><td style="padding:8px;border:1px solid #ddd;font-weight:bold;">Owner</td><td style="padding:8px;border:1px solid #ddd;">${item.owner}</td></tr>
              <tr><td style="padding:8px;border:1px solid #ddd;font-weight:bold;">Refurb Status</td><td style="padding:8px;border:1px solid #ddd;">${item.refurbStatus}</td></tr>
              <tr><td style="padding:8px;border:1px solid #ddd;font-weight:bold;">Date Returned</td><td style="padding:8px;border:1px solid #ddd;">${dateStr}</td></tr>
            </table>
          `
        })
      });

      setItems(prev => prev.map(v => v._id === item._id ? updatedVessel : v));
      setViewingItem(updatedVessel);
      alert('Vessel booked out successfully. Notification email sent.');
    } catch (error) {
      console.error('Book out failed:', error);
      alert(`Failed to book out vessel: ${error.message}`);
    } finally {
      setIsBookingOut(false);
    }
  };

  return (
    <PVSPageLayout className="p-4">
      <div className="max-w-6xl mx-auto min-h-[calc(100vh-2rem)]">
      <div className="pvs-glass min-h-[calc(100vh-2rem)]">
        <div className="bg-pvs-accent/10 border-b border-pvs-accent/20 p-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-white text-2xl font-bold">PVS Vessel Inventory</h2>
            <button
              onClick={handleHomeNavigation}
              className="flex items-center justify-center w-12 h-12 pvs-glass pvs-glass-hover"
              title="Return to Homepage"
            >
              <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Search Section */}
          <div className="mb-6 bg-pvs-card-light border border-pvs-border p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-pvs-accent">Search Vessels</h3>
            <div className="space-y-4">
              {searchCriteria.map((criteria, index) => (
                <div key={index} className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                      Search Field
                    </label>
                    <select
                      value={criteria.field}
                      onChange={(e) => handleSearchFieldChange(index, 'field', e.target.value)}
                      className="w-full p-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg"
                    >
                      <option value="">Select field to search</option>
                      <option value="serialNumber">Serial Number</option>
                      <option value="vesselSize">Vessel Size </option>
                      <option value="vesselManufacturer">Vessel Manufacturer</option>
                      <option value="designPressure">Design Pressure</option>
                      <option value="owner">Owner</option>
                      <option value="refurbStatus">Refurb Status</option>
                      <option value="scrapPreparation">Scrap Preparation</option>
                      <option value="workDone">Work Done</option>
                      <option value="engineerComments">Engineer Comments</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                      Search Value
                    </label>
                    {criteria.field === 'refurbStatus' ? (
                      <select
                        value={criteria.value}
                        onChange={(e) => handleSearchFieldChange(index, 'value', e.target.value)}
                        className="w-full p-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg"
                      >
                        <option value="">Any Status</option>
                        {refurbStatusOptions.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    ) : criteria.field === 'scrapPreparation' ? (
                      <select
                        value={criteria.value}
                        onChange={(e) => handleSearchFieldChange(index, 'value', e.target.value)}
                        className="w-full p-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg"
                      >
                        <option value="">Any preparation</option>
                        {SCRAP_PREP_OPTIONS.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={criteria.value}
                        onChange={(e) => handleSearchFieldChange(index, 'value', e.target.value)}
                        className="w-full p-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg"
                        placeholder={`Enter ${criteria.field || 'value'}`}
                      />
                    )}
                  </div>
                  <div className="flex gap-2">
                    {index === searchCriteria.length - 1 && (
                      <button
                        type="button"
                        onClick={addSearchField}
                        className="px-3 py-2 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-all duration-200"
                      >
                        Add Field
                      </button>
                    )}
                    {searchCriteria.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSearchField(index)}
                        className="px-3 py-2 bg-red-900/30 text-red-400 rounded-lg hover:bg-red-900/50"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={handleSearch}
                  className="bg-pvs-accent text-pvs-bg font-semibold px-4 py-2 rounded-lg flex items-center hover:bg-pvs-accent-dim transition-all duration-200"
                >
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </button>
                <button
                  onClick={clearSearch}
                  className="text-pvs-text-muted hover:text-white px-4 py-2 rounded-lg flex items-center"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>

          {/* Add New Vessel / Scan QR Buttons */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-3">
              {canAdd && (
                <button 
                  onClick={() => {
                    setFormData({ ...initialFormState, dateReceived: new Date().toISOString().split('T')[0] });
                    setEditingItem(null);
                    setShowAddDialog(true);
                  }}
                  className="bg-pvs-accent text-pvs-bg font-semibold px-4 py-2 rounded-lg flex items-center hover:bg-pvs-accent-dim transition-all duration-200"
                >
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Add New Vessel
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => setShowScanner(true)}
                  className="bg-pvs-accent text-pvs-bg font-semibold px-4 py-2 rounded-lg flex items-center hover:bg-pvs-accent-dim transition-all duration-200"
                >
                  <ScanLine className="mr-2 h-4 w-4" />
                  Scan QR
                </button>
              )}
            </div>
          </div>

          {/* Results Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-pvs-border">
              <thead className="bg-pvs-card-light">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Serial Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Owner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Date Received</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Date Returned</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Refurb Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Pre Insp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Paint Insp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Tank Cert</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Final QC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Gas Free</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-pvs-text-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-pvs-card divide-y divide-pvs-border">
              {items.map((item) => (
    <tr key={item._id} className="hover:bg-pvs-card-light">
      <td className="px-6 py-4 whitespace-nowrap text-white">{item.serialNumber}</td>
      <td className="px-6 py-4 whitespace-nowrap text-white">{item.vesselSize}</td>
      <td className="px-6 py-4 whitespace-nowrap text-white">{item.owner}</td>
      <td className="px-6 py-4 whitespace-nowrap text-white">{formatDisplayDate(item.dateReceived)}</td>
      <td className="px-6 py-4 whitespace-nowrap text-white">{formatDisplayDate(item.dateReturned)}</td>
      <td className="px-6 py-4 whitespace-nowrap text-white">
        {item.refurbStatus}
        {item.scrapPreparation && (
          <div className="text-xs text-pvs-text-muted">{item.scrapPreparation}</div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-xs">{renderFormTick(item, 'preInspection')}</td>
      <td className="px-6 py-4 whitespace-nowrap text-xs">{renderFormTick(item, 'paintQualityInspection')}</td>
      <td className="px-6 py-4 whitespace-nowrap text-xs">{renderFormTick(item, 'tankCertification')}</td>
      <td className="px-6 py-4 whitespace-nowrap text-xs">{renderFormTick(item, 'finalQCChecklist')}</td>
      <td className="px-6 py-4 whitespace-nowrap text-xs">{renderFormTick(item, 'gasFreeCheck')}</td>
      <td className="px-6 py-4 whitespace-nowrap space-x-2">
        {canEdit && (
        <button
          onClick={() => handleEdit(item)}
          className="text-pvs-accent hover:text-pvs-accent-dim p-1 transition-colors duration-200"
        >
          <Edit className="h-4 w-4" />
        </button>
        )}
        {canDelete && (
          <button
            onClick={() => handleDelete(item._id)}
            className="text-red-400 hover:text-red-300 p-1"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
          <button
    onClick={() => handleViewDetails(item)}
    className="text-pvs-text-muted hover:text-white p-1"
  >
    <Eye className="h-4 w-4" />
  </button>
      </td>
    </tr>
  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div>

      {/* Modals rendered via portal to escape backdrop-filter containing block */}
      {showAddDialog && ReactDOM.createPortal(
            <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-70">
              <div className="flex min-h-full items-center justify-center p-4">
              <div className="bg-pvs-card border border-pvs-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-white">
                    {editingItem ? 'Edit Vessel Record' : 'Add New Vessel'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowAddDialog(false);
                      setEditingItem(null);
                      setShowSerialConfirm(false);
                      setSerialConfirmValue('');
                      setSerialConfirmError('');
                      setShowScrapPrepHelp(false);
                    }}
                    className="text-pvs-text-muted hover:text-white"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmit} onReset={preventFormReset} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                  <div>
    <label className="block text-sm font-medium text-pvs-text-muted mb-1">
        Serial Number*
    </label>
    <input
        ref={serialInputRef}
        type="text"
        name="serialNumber"
        value={formData.serialNumber}
        onChange={handleInputChange}
         autoComplete="off"  
        className={`w-full p-2 bg-pvs-input-bg border rounded-lg ${showSerialConfirm && !ownerIsCalor ? 'text-transparent' : 'text-white'} ${errors.serialNumber ? 'border-red-500' : 'border-pvs-input-border'}`}
    />
    {errors.serialNumber && (
        <p className="text-red-400 text-xs mt-1">{errors.serialNumber}</p>
    )}
</div>
                    <div>
                      <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                        Size (Water Capacity in L)*
                      </label>
                      <input
                        type="text"
                        name="vesselSize"
                        value={formData.vesselSize}
                        onChange={handleInputChange}
                        required
                        min="1"
                        className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white ${
                            errors.vesselSize ? 'border-red-500' : 'border-pvs-input-border'
                        }`}
                      />
                      {errors.vesselSize && (
        <p className="text-red-400 text-xs mt-1">{errors.vesselSize}</p>
    )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                        Vessel Manufacturer{!editingItem ? '*' : ''}
                      </label>
                      <input
                        type="text"
                        name="vesselManufacturer"
                        value={formData.vesselManufacturer}
                        onChange={handleInputChange}
                        required={!editingItem}
                        className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white ${
                            errors.vesselManufacturer ? 'border-red-500' : 'border-pvs-input-border'
                        }`}
                      />
                      {errors.vesselManufacturer && (
                        <p className="text-red-400 text-xs mt-1">{errors.vesselManufacturer}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                        Design Pressure (bar){!editingItem ? '*' : ''}
                      </label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        name="designPressure"
                        value={formData.designPressure}
                        onChange={handleInputChange}
                        required={!editingItem}
                        className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white ${
                            errors.designPressure ? 'border-red-500' : 'border-pvs-input-border'
                        }`}
                      />
                      {errors.designPressure && (
                        <p className="text-red-400 text-xs mt-1">{errors.designPressure}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                        Date of Manufacture{!editingItem ? '*' : ''}
                      </label>
                      <input
                        type="date"
                        name="dateOfManufacture"
                        value={formData.dateOfManufacture || ''}
                        onChange={handleInputChange}
                        required={!editingItem}
                        className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white ${
                            errors.dateOfManufacture ? 'border-red-500' : 'border-pvs-input-border'
                        }`}
                      />
                      {errors.dateOfManufacture && (
                        <p className="text-red-400 text-xs mt-1">{errors.dateOfManufacture}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                        Owner*
                      </label>
                      <input
                        type="text"
                        name="owner"
                        value={formData.owner}
                        onChange={handleInputChange}
                        required
                        className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white ${
                            errors.owner ? 'border-red-500' : 'border-pvs-input-border'
                        }`}
                      />
                      {errors.owner && (
                        <p className="text-red-400 text-xs mt-1">{errors.owner}</p>
                      )}
                    </div>
                    {ownerIsCalor && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                            Vessel Type Code*
                          </label>
                          <input
                            type="number"
                            name="vesselTypeCode"
                            value={formData.vesselTypeCode}
                            onChange={handleInputChange}
                            required
                            className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white ${
                                errors.vesselTypeCode ? 'border-red-500' : 'border-pvs-input-border'
                            }`}
                          />
                          {errors.vesselTypeCode && (
                            <p className="text-red-400 text-xs mt-1">{errors.vesselTypeCode}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                            Calor Serial Number*
                          </label>
                          <input
                            ref={calorSerialInputRef}
                            type="text"
                            name="calorSerialNumber"
                            value={formData.calorSerialNumber}
                            onChange={handleInputChange}
                            required
                            autoComplete="off"
                            className={`w-full p-2 bg-pvs-input-bg border rounded-lg ${
                                showSerialConfirm && ownerIsCalor ? 'text-transparent' : 'text-white'
                            } ${
                                errors.calorSerialNumber ? 'border-red-500' : 'border-pvs-input-border'
                            }`}
                          />
                          {errors.calorSerialNumber && (
                            <p className="text-red-400 text-xs mt-1">{errors.calorSerialNumber}</p>
                          )}
                        </div>
                      </>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                        Refurb Status*
                      </label>
                      <select
                        name="refurbStatus"
                        value={formData.refurbStatus}
                        onChange={handleInputChange}
                        disabled={editingScrappedVessel && !isAdmin}
                        className={`w-full p-2 bg-pvs-input-bg border text-white rounded-lg disabled:opacity-60 disabled:cursor-not-allowed ${
                            errors.refurbStatus ? 'border-red-500' : 'border-pvs-input-border'
                        }`}
                      >
                        {refurbStatusOptions.map(status => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                      {editingScrappedVessel && (
                        <p className="text-amber-400 text-xs mt-1">
                          {isAdmin
                            ? 'This vessel is scrapped. Changing its status needs a reason, which is added to the engineer comments.'
                            : 'This vessel is scrapped and locked. Only an admin can change its refurb status.'}
                        </p>
                      )}
                      {errors.refurbStatus && (
                        <p className="text-red-400 text-xs mt-1">{errors.refurbStatus}</p>
                      )}
                    </div>
                    {leavingScrap && isAdmin && (
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                          Reason for changing from Scrap*
                        </label>
                        <textarea
                          name="scrapStatusChangeReason"
                          value={formData.scrapStatusChangeReason}
                          onChange={handleInputChange}
                          className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white h-20 ${
                              errors.scrapStatusChangeReason ? 'border-red-500' : 'border-pvs-input-border'
                          }`}
                        />
                        {errors.scrapStatusChangeReason && (
                          <p className="text-red-400 text-xs mt-1">{errors.scrapStatusChangeReason}</p>
                        )}
                      </div>
                    )}
                    {(showScrapPrep || needsScrapPrepReason) && (
                      <div className="col-span-2">
                        {showScrapPrep && (
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <label className="block text-sm font-medium text-pvs-text-muted">
                                Scrap preparation*
                              </label>
                              <button
                                type="button"
                                onClick={() => setShowScrapPrepHelp(true)}
                                className="text-pvs-text-muted hover:text-white"
                                aria-label="Explain Calor Prepared, PVS Prepared and PVS Inspection Failure"
                              >
                                <CircleHelp className="h-4 w-4" />
                              </button>
                            </div>
                            <div
                              className={`flex flex-col sm:flex-row rounded-lg overflow-hidden border ${
                                errors.scrapPreparation ? 'border-red-500' : 'border-pvs-input-border'
                              }`}
                              role="group"
                              aria-label="Scrap preparation"
                            >
                              {SCRAP_PREP_OPTIONS.map((option) => {
                                const selected = formData.scrapPreparation === option;
                                return (
                                  <button
                                    key={option}
                                    type="button"
                                    disabled={scrapPrepLockedForUser}
                                    aria-pressed={selected}
                                    onClick={() => {
                                      if (scrapPrepLockedForUser) return;
                                      setFormData(prev => ({ ...prev, scrapPreparation: option }));
                                      if (errors.scrapPreparation) {
                                        setErrors(prev => ({ ...prev, scrapPreparation: null }));
                                      }
                                    }}
                                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                                      selected
                                        ? 'bg-pvs-accent text-pvs-bg'
                                        : 'bg-pvs-input-bg text-white hover:bg-pvs-card-light'
                                    }`}
                                  >
                                    {option}
                                  </button>
                                );
                              })}
                            </div>
                            {scrapPrepLockedForUser && (
                              <p className="text-amber-400 text-xs mt-1">
                                Calor Prepared and PVS Prepared are locked. Only an admin can change this, and a reason is added to the engineer comments.
                              </p>
                            )}
                            {errors.scrapPreparation && (
                              <p className="text-red-400 text-xs mt-1">{errors.scrapPreparation}</p>
                            )}
                          </>
                        )}
                        {needsScrapPrepReason && (
                          <div className={showScrapPrep ? 'mt-3' : ''}>
                            {!showScrapPrep && (
                              <p className="text-amber-400 text-xs mb-2">
                                Changing the owner away from Calor Gas clears the locked scrap preparation. A reason is required and is added to the engineer comments.
                              </p>
                            )}
                            <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                              Reason for changing scrap preparation*
                            </label>
                            <textarea
                              name="scrapPreparationChangeReason"
                              value={formData.scrapPreparationChangeReason}
                              onChange={handleInputChange}
                              className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white h-20 ${
                                errors.scrapPreparationChangeReason ? 'border-red-500' : 'border-pvs-input-border'
                              }`}
                            />
                            {showScrapPrep && (
                              <p className="text-pvs-text-muted text-xs mt-1">
                                This reason is added to the engineer comments.
                              </p>
                            )}
                            {errors.scrapPreparationChangeReason && (
                              <p className="text-red-400 text-xs mt-1">{errors.scrapPreparationChangeReason}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
    <label className="block text-sm font-medium text-pvs-text-muted mb-1">
        Date Received*
    </label>
    <input
        type="date"
        name="dateReceived"
        value={formData.dateReceived || ''}
        onChange={handleInputChange}
        required
        className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white ${
            errors.dateReceived ? 'border-red-500' : 'border-pvs-input-border'
        }`}
    />
    {errors.dateReceived && (
        <p className="text-red-400 text-xs mt-1">{errors.dateReceived}</p>
    )}
</div>

{editingItem && (
  <>
<div>
    <label className="block text-sm font-medium text-pvs-text-muted mb-1">
        Date Returned
    </label>
    <input
        type="date"
        name="dateReturned"
        value={formData.dateReturned || ''}
        onChange={handleInputChange}
        className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white ${
            errors.dateReturned ? 'border-red-500' : 'border-pvs-input-border'
        }`}
    />
    {errors.dateReturned && (
        <p className="text-red-400 text-xs mt-1">{errors.dateReturned}</p>
    )}
</div>

<div>
    <label className="block text-sm font-medium text-pvs-text-muted mb-1">
        Date Refurbed
    </label>
    <input
        type="date"
        name="dateRefurbed"
        value={formData.dateRefurbed || ''}
        onChange={handleInputChange}
        className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white ${
            errors.dateRefurbed ? 'border-red-500' : 'border-pvs-input-border'
        }`}
    />
    {errors.dateRefurbed && (
        <p className="text-red-400 text-xs mt-1">{errors.dateRefurbed}</p>
    )}
</div>
  </>
)}
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                      Work Done
                    </label>
                    <textarea
                      name="workDone"
                      value={formData.workDone}
                      onChange={handleInputChange}
                      className="w-full p-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg h-24"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                      Engineer Comments
                    </label>
                    <textarea
                      name="engineerComments"
                      value={formData.engineerComments}
                      onChange={handleInputChange}
                      className="w-full p-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg h-24"
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                  
<button
  type="button"
  onClick={() => {
    setShowAddDialog(false);
    setEditingItem(null);
    setFormData(initialFormState);
    setErrors({});
    setShowSerialConfirm(false);
    setSerialConfirmValue('');
    setSerialConfirmError('');
    setShowScrapPrepHelp(false);
  }}
  className="px-4 py-2 bg-pvs-card-light text-pvs-text-muted border border-pvs-border hover:bg-pvs-border rounded-md"
>
  Cancel
</button>
                    <button
    type="submit"
    disabled={isSubmitting}
    className={`px-4 py-2 ${
        isSubmitting 
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
            : 'bg-pvs-accent text-pvs-bg font-semibold hover:bg-pvs-accent-dim'
    } rounded-md`}
>
    {isSubmitting ? (
        <span className="flex items-center">
            <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                {/* Loading spinner SVG */}
            </svg>
            {editingItem ? 'Updating...' : 'Adding...'}
        </span>
    ) : (
        editingItem ? 'Update Vessel' : 'Add Vessel'
    )}
</button>


                  </div>
                </form>
              </div>
              </div>
            </div>
          , document.body)}

      {showScrapPrepHelp && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[70] overflow-y-auto bg-black bg-opacity-80">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="bg-pvs-card border border-pvs-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-start gap-4 mb-4">
                <h3 className="text-lg font-bold text-white">Scrap preparation</h3>
                <button
                  type="button"
                  onClick={() => setShowScrapPrepHelp(false)}
                  className="text-pvs-text-muted hover:text-white text-xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="space-y-4">
                {SCRAP_PREP_HELP.map((item) => (
                  <div key={item.title}>
                    <h4 className="text-sm font-semibold text-pvs-accent mb-1">{item.title}</h4>
                    <p className="text-sm text-gray-300">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      , document.body)}

      {showSerialConfirm && ReactDOM.createPortal(
            <div className="fixed inset-0 z-[60] overflow-y-auto bg-black bg-opacity-90">
              <div className="flex min-h-full items-center justify-center p-4">
                <div className="bg-pvs-card border border-pvs-border rounded-lg max-w-md w-full p-6">
                  <h3 className="text-lg font-bold text-white mb-2">
                    Confirm {ownerIsCalor ? 'Calor ' : ''}Serial Number
                  </h3>
                  <p className="text-sm text-pvs-text-muted mb-4">
                    Read the number from the vessel and type it again in full. The number you
                    entered is hidden and copy/paste is disabled so it has to be re-checked.
                    If it does not match exactly you will have to enter it again from the start.
                  </p>

                  <form onSubmit={handleSerialConfirmSubmit}>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-1">
                      Re-enter Serial Number*
                    </label>
                    <input
                      ref={serialConfirmInputRef}
                      type="text"
                      value={serialConfirmValue}
                      onChange={(e) => {
                        setSerialConfirmValue(e.target.value);
                        if (serialConfirmError) setSerialConfirmError('');
                      }}
                      onPaste={blockClipboard}
                      onCopy={blockClipboard}
                      onCut={blockClipboard}
                      onDrop={blockClipboard}
                      onDragStart={blockClipboard}
                      onContextMenu={(e) => e.preventDefault()}
                      autoFocus
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      className={`w-full p-2 bg-pvs-input-bg border rounded-lg text-white ${
                        serialConfirmError ? 'border-red-500' : 'border-pvs-input-border'
                      }`}
                    />
                    {serialConfirmError && (
                      <p className="text-red-400 text-xs mt-1">{serialConfirmError}</p>
                    )}

                    <div className="flex justify-end space-x-2 mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowSerialConfirm(false);
                          setSerialConfirmValue('');
                          setSerialConfirmError('');
                        }}
                        className="px-4 py-2 bg-pvs-card-light text-pvs-text-muted border border-pvs-border hover:bg-pvs-border rounded-md"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!serialConfirmValue.trim()}
                        className={`px-4 py-2 rounded-md ${
                          serialConfirmValue.trim()
                            ? 'bg-pvs-accent text-pvs-bg font-semibold hover:bg-pvs-accent-dim'
                            : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        Confirm &amp; Save
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          , document.body)}

      {viewingItem && ReactDOM.createPortal(
  <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-70">
    <div className="flex min-h-full items-center justify-center p-4">
    <div className="bg-pvs-card border border-pvs-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-white">Vessel Details</h3>
        <button
          onClick={closeViewModal}
          className="text-pvs-text-muted hover:text-white"
        >
          ×
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-pvs-text-muted">Serial Number</label>
          <p className="mt-1 text-sm text-white">{viewingItem.serialNumber}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-pvs-text-muted">Size</label>
          <p className="mt-1 text-sm text-white">{viewingItem.vesselSize} Litres</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-pvs-text-muted">Vessel Manufacturer</label>
          <p className="mt-1 text-sm text-white">{viewingItem.vesselManufacturer || 'Not specified'}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-pvs-text-muted">Design Pressure</label>
          <p className="mt-1 text-sm text-white">
            {viewingItem.designPressure !== undefined && viewingItem.designPressure !== null && viewingItem.designPressure !== ''
              ? `${viewingItem.designPressure} bar`
              : 'Not specified'}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-pvs-text-muted">Date of Manufacture</label>
          <p className="mt-1 text-sm text-white">
            {formatDisplayDate(viewingItem.dateOfManufacture) || 'Not specified'}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-pvs-text-muted">Owner</label>
          <p className="mt-1 text-sm text-white">{viewingItem.owner}</p>
        </div>
        {isCalorOwner(viewingItem.owner) && (
          <>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted">Vessel Type Code</label>
              <p className="mt-1 text-sm text-white">
                {viewingItem.vesselTypeCode ?? 'Not specified'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted">Calor Serial Number</label>
              <p className="mt-1 text-sm text-white">
                {viewingItem.calorSerialNumber || 'Not specified'}
              </p>
            </div>
          </>
        )}
        <div>
          <label className="block text-sm font-medium text-pvs-text-muted">Refurb Status</label>
          <p className="mt-1 text-sm text-white">{viewingItem.refurbStatus}</p>
        </div>
        {(viewingItem.scrapPreparation || (isCalorOwner(viewingItem.owner) && viewingItem.refurbStatus === 'Scrap')) && (
          <div>
            <label className="block text-sm font-medium text-pvs-text-muted">Scrap Preparation</label>
            <p className="mt-1 text-sm text-white">{viewingItem.scrapPreparation || 'Not recorded'}</p>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-pvs-text-muted">Date Received</label>
          <p className="mt-1 text-sm text-white">
            {formatDisplayDate(viewingItem.dateReceived) || 'Not specified'}
          </p>
        </div>
        <div>
    <label className="block text-sm font-medium text-pvs-text-muted">Date Refurbed</label>
    <p className="mt-1 text-sm text-white">
        {formatDisplayDate(viewingItem.dateRefurbed) || 'Not specified'}
    </p>
</div>
        <div>
          <label className="block text-sm font-medium text-pvs-text-muted">Date Returned</label>
          <p className="mt-1 text-sm text-white">
            {formatDisplayDate(viewingItem.dateReturned) || 'Not specified'}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-pvs-text-muted">Work Done</label>
          <p className="mt-1 text-sm text-gray-300 whitespace-pre-wrap">
            {viewingItem.workDone || 'No work details recorded'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-pvs-text-muted">Engineer Comments</label>
          <p className="mt-1 text-sm text-gray-300 whitespace-pre-wrap">
            {viewingItem.engineerComments || 'No comments recorded'}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-pvs-text-muted mb-2">Form Completion</label>
          <div className="space-y-2 text-sm text-white">
            {[
              { key: 'preInspection', label: 'Pre Inspection' },
              { key: 'paintQualityInspection', label: 'Paint Quality Inspection' },
              { key: 'tankCertification', label: 'Tank Certification' },
              { key: 'finalQCChecklist', label: 'Final QC Checklist' },
              { key: 'gasFreeCheck', label: 'Gas Free Check' }
            ].map(({ key, label }) => {
              const status = getFormStatus(viewingItem, key);
              return (
                <div key={key}>
                  {label}: {status.completed ? (
                    <span className="text-pvs-accent font-medium">
                      ✓ {formatDisplayDate(status.completedAt)}
                      {status.completedBy ? ` — ${status.completedBy}` : ''}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {(isAdmin || userRole === 'readonly') && (
        <div className="mt-6 border-t border-pvs-border pt-4">
          <h4 className="text-base font-semibold text-white mb-3">Audit & Traceability</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted">Created By</label>
              <p className="mt-1 text-sm text-white">{viewingItem.createdByUsername || 'Not recorded'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted">Created At</label>
              <p className="mt-1 text-sm text-white">
                {viewingItem.createdAt ? new Date(viewingItem.createdAt).toLocaleString() : 'Not recorded'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted">Last Edited By</label>
              <p className="mt-1 text-sm text-white">{viewingItem.updatedByUsername || 'Not recorded'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted">Last Edited At</label>
              <p className="mt-1 text-sm text-white">
                {viewingItem.updatedAt ? new Date(viewingItem.updatedAt).toLocaleString() : 'Not recorded'}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-pvs-text-muted mb-2">History Timeline</label>
            {isAuditLoading && (
              <p className="text-sm text-pvs-text-muted">Loading audit history...</p>
            )}
            {!isAuditLoading && auditError && (
              <p className="text-sm text-red-400">{auditError}</p>
            )}
            {!isAuditLoading && !auditError && auditHistory.length === 0 && (
              <p className="text-sm text-pvs-text-muted">No audit history recorded yet.</p>
            )}
            {!isAuditLoading && !auditError && auditHistory.length > 0 && (
              <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                {auditHistory.map((entry) => (
                  <div key={entry._id} className="rounded-md border border-pvs-border p-3 bg-pvs-card-light">
                    <p className="text-sm font-semibold text-white">
                      {entry.action?.toUpperCase() || 'UPDATE'}
                    </p>
                    <p className="text-xs text-pvs-text-muted mt-1">
                      {entry.actorName || 'Unknown user'} ({entry.actorRole || 'standard'}) - {entry.eventAt ? new Date(entry.eventAt).toLocaleString() : 'Unknown time'}
                    </p>
                    {Array.isArray(entry.changes) && entry.changes.length > 0 && (
                      <ul className="mt-2 text-xs text-gray-300 space-y-1">
                        {entry.changes.slice(0, 8).map((change, index) => (
                          <li key={`${entry._id}-${change.field}-${index}`}>
                            <span className="font-medium">{change.field}</span>: {String(change.oldValue ?? 'null')} {'->'} {String(change.newValue ?? 'null')}
                          </li>
                        ))}
                        {entry.changes.length > 8 && (
                          <li className="text-pvs-text-dim">...and {entry.changes.length - 8} more changes</li>
                        )}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="mt-6 border-t border-pvs-border pt-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6">
            <div className="flex flex-col items-center">
              <label className="block text-sm font-medium text-pvs-text-muted mb-2">Vessel QR Code</label>
              <div id="qr-code-svg-container" className="bg-white p-3 rounded-lg">
                <QRCodeSVG value={viewingItem.serialNumber} size={140} />
              </div>
              <button
                onClick={() => handlePrintQR(viewingItem.serialNumber)}
                className="mt-2 px-3 py-1.5 bg-pvs-card-light text-pvs-text-muted border border-pvs-border rounded-md hover:bg-pvs-border flex items-center text-sm"
              >
                <Printer className="mr-1.5 h-3.5 w-3.5" />
                Print QR
              </button>
            </div>

            {openedViaQrScan && (
              <div className="flex-1">
                {allFormsComplete(viewingItem) ? (
                  <button
                    onClick={() => handleBookOut(viewingItem)}
                    disabled={isBookingOut}
                    className={`px-5 py-2.5 rounded-lg font-semibold flex items-center transition-all duration-200 ${
                      isBookingOut
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    <PackageCheck className="mr-2 h-5 w-5" />
                    {isBookingOut ? 'Booking Out...' : 'Book Out'}
                  </button>
                ) : (
                  <div>
                    <button
                      disabled
                      className="px-5 py-2.5 rounded-lg font-semibold flex items-center bg-gray-700 text-gray-500 cursor-not-allowed"
                    >
                      <PackageCheck className="mr-2 h-5 w-5" />
                      Book Out
                    </button>
                    <p className="mt-2 text-xs text-red-400">
                      Cannot book out — the following forms are incomplete:
                    </p>
                    <ul className="mt-1 text-xs text-red-400 list-disc list-inside">
                      {getIncompleteFormNames(viewingItem).map(name => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button
          onClick={closeViewModal}
          className="px-4 py-2 bg-pvs-card-light text-pvs-text-muted border border-pvs-border rounded-md hover:bg-pvs-border"
        >
          Close
        </button>
      </div>
    </div>
    </div>
  </div>
, document.body)}

      {showScanner && ReactDOM.createPortal(
            <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-70">
              <div className="flex min-h-full items-center justify-center p-4">
              <div className="bg-pvs-card border border-pvs-border rounded-lg max-w-md w-full p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-white flex items-center">
                    <QrCode className="mr-2 h-5 w-5 text-pvs-accent" />
                    Scan Vessel QR Code
                  </h3>
                  <button
                    onClick={stopScanner}
                    className="text-pvs-text-muted hover:text-white text-xl"
                  >
                    ×
                  </button>
                </div>
                <div id={scannerDivId} className="w-full rounded-lg overflow-hidden" />
                {scanError && (
                  <p className="mt-3 text-sm text-red-400">{scanError}</p>
                )}
                <p className="mt-3 text-xs text-pvs-text-muted text-center">
                  Point your camera at a vessel QR code to look up its details.
                </p>
                <div className="mt-4 flex justify-between">
                  <button
                    onClick={startScanner}
                    className="px-4 py-2 bg-pvs-accent text-pvs-bg font-semibold rounded-md hover:bg-pvs-accent-dim transition-all duration-200"
                  >
                    Start Camera
                  </button>
                  <button
                    onClick={stopScanner}
                    className="px-4 py-2 bg-pvs-card-light text-pvs-text-muted border border-pvs-border rounded-md hover:bg-pvs-border"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              </div>
            </div>
      , document.body)}
    </PVSPageLayout>
  );
};

export default LPGInventorySystem;
